import type { TaskListItem, TaskOperationalHistoryEntry } from '../../shared/types/task';
import type { UnifiedWorkItem } from '../types/workItem';
import { formatRelativeTime } from './taskDisplayUtils';
import {
  getOperationalRiskLevel,
  getOperationalRiskRank,
  getRemainingMinutesFromDeadline,
  getTaskTotalSlaMinutes,
} from '../../shared/utils/operationalRisk';

const PRIORITY_ORDER: Record<string, number> = {
  critica: 4,
  alta: 3,
  media: 2,
  baixa: 1,
};

const SHIFT_MINUTES = 8 * 60;

export function compareExecutionPriority(left: TaskListItem, right: TaskListItem): number {
  const leftRiskRank = getOperationalRiskRank(getOperationalRiskLevel(
    getRemainingMinutesFromDeadline(left.slaDeadline),
    getTaskTotalSlaMinutes(left),
  ));
  const rightRiskRank = getOperationalRiskRank(getOperationalRiskLevel(
    getRemainingMinutesFromDeadline(right.slaDeadline),
    getTaskTotalSlaMinutes(right),
  ));
  if (leftRiskRank !== rightRiskRank) return rightRiskRank - leftRiskRank;

  const leftRemaining = getRemainingMinutesFromDeadline(left.slaDeadline);
  const rightRemaining = getRemainingMinutesFromDeadline(right.slaDeadline);
  if (leftRemaining === null && rightRemaining !== null) return 1;
  if (leftRemaining !== null && rightRemaining === null) return -1;
  if (leftRemaining !== null && rightRemaining !== null && leftRemaining !== rightRemaining) {
    return leftRemaining - rightRemaining;
  }

  const leftPriority = PRIORITY_ORDER[(left.priority ?? '').toLowerCase()] ?? 0;
  const rightPriority = PRIORITY_ORDER[(right.priority ?? '').toLowerCase()] ?? 0;
  if (leftPriority !== rightPriority) return rightPriority - leftPriority;

  return new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime();
}

export interface NextExecutionAudit {
  task: TaskListItem | null;
  motivo: string;
  posicaoFila: number | null;
  sla: string;
  criticidade: string;
}

function pickBestByOperationalTiebreak(candidates: TaskListItem[]): TaskListItem | null {
  if (candidates.length === 0) return null;
  return [...candidates].sort(compareExecutionPriority)[0] ?? null;
}

function resolveSelectionReason(task: TaskListItem): string {
  if (task.status === 'in_progress') return 'Atividade em execução — continuar';
  if (task.status === 'paused') return 'Atividade pausada — retomar';
  return 'Cabeça da fila de workflow — ordem de abertura';
}

function resolveTaskCriticalityLabel(task: TaskListItem): string {
  const remaining = getRemainingMinutesFromDeadline(task.slaDeadline);
  const risk = getOperationalRiskLevel(remaining, getTaskTotalSlaMinutes(task));
  if (task.isOverdue || risk === 'CRITICO') return 'CRÍTICA';
  if (risk === 'ALERTA') return 'ALERTA';
  return resolveOperationalPriority(task).operationalLabel.toUpperCase();
}

export function resolveNextExecutableTask(
  claimedTasks: TaskListItem[],
  queueTasks: TaskListItem[],
  inboxRailTasks: TaskListItem[],
): NextExecutionAudit {
  const active = [...claimedTasks, ...queueTasks].filter(t => t.status !== 'completed');
  const fifoOrdered = buildFifoQueue(claimedTasks, queueTasks, 999);

  if (active.length > 0) {
    const inProgress = active.filter(t => t.status === 'in_progress');
    const paused = active.filter(t => t.status === 'paused');
    const task =
      pickBestByOperationalTiebreak(inProgress)
      ?? pickBestByOperationalTiebreak(paused)
      ?? fifoOrdered[0]
      ?? null;

    if (!task) {
      return { task: null, motivo: 'Nenhuma tarefa ativa', posicaoFila: null, sla: '—', criticidade: '—' };
    }
    const fifoIndex = fifoOrdered.findIndex(t => t.id === task.id);
    return {
      task,
      motivo: resolveSelectionReason(task),
      posicaoFila: fifoIndex >= 0 ? fifoIndex + 1 : null,
      sla: formatTaskSlaDetail(task),
      criticidade: resolveTaskCriticalityLabel(task),
    };
  }

  const inboxActive = inboxRailTasks.filter(t => t.status !== 'completed');
  const task =
    pickBestByOperationalTiebreak(inboxActive.filter(t => t.status === 'in_progress'))
    ?? pickBestByOperationalTiebreak(inboxActive.filter(t => t.status === 'paused'))
    ?? inboxRailTasks[0]
    ?? null;
  if (!task) {
    return { task: null, motivo: 'Carteira vazia', posicaoFila: null, sla: '—', criticidade: '—' };
  }
  return {
    task,
    motivo: 'Primeiro item da carteira (ação ou problema)',
    posicaoFila: 1,
    sla: formatTaskSlaDetail(task),
    criticidade: resolveTaskCriticalityLabel(task),
  };
}

export function logNextExecutionSelection(audit: NextExecutionAudit): void {
  if (import.meta.env.PROD || !audit.task) return;
  console.info('[MinhaMesa:FocoAgora]', {
    tarefa: audit.task.stageName ?? audit.task.instanceTitle,
    id: audit.task.id,
    motivo: audit.motivo,
    posicaoNaFila: audit.posicaoFila,
    sla: audit.sla,
    criticidade: audit.criticidade,
    status: audit.task.status,
  });
}

export function pickNextExecutableTask(
  claimedTasks: TaskListItem[],
  queueTasks: TaskListItem[],
  inboxRailTasks: TaskListItem[],
): TaskListItem | null {
  return resolveNextExecutableTask(claimedTasks, queueTasks, inboxRailTasks).task;
}

export function buildFifoQueue(
  claimedTasks: TaskListItem[],
  queueTasks: TaskListItem[],
  limit = 8,
): TaskListItem[] {
  const seen = new Set<string>();
  const combined: TaskListItem[] = [];
  for (const t of [...claimedTasks, ...queueTasks]) {
    if (t.status === 'completed' || seen.has(t.id)) continue;
    seen.add(t.id);
    combined.push(t);
  }
  return combined
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    .slice(0, limit);
}

export interface AttentionItem {
  id: string;
  title: string;
  reason: string;
  slaDetail?: string;
  tone: 'critical' | 'warning';
  taskId: string;
}

export function buildAttentionItems(
  claimedTasks: TaskListItem[],
  queueTasks: TaskListItem[],
  unifiedWorkItems: UnifiedWorkItem[],
): AttentionItem[] {
  const items: AttentionItem[] = [];
  const seen = new Set<string>();

  const pushTask = (task: TaskListItem, reason: string, tone: AttentionItem['tone']) => {
    if (seen.has(task.id)) return;
    seen.add(task.id);
    items.push({
      id: task.id,
      taskId: task.id,
      title: task.stageName ?? task.instanceTitle ?? 'Tarefa',
      reason,
      slaDetail: formatTaskSlaDetail(task),
      tone,
    });
  };

  for (const task of [...claimedTasks, ...queueTasks]) {
    if (task.status === 'completed') continue;
    const risk = getOperationalRiskLevel(
      getRemainingMinutesFromDeadline(task.slaDeadline),
      getTaskTotalSlaMinutes(task),
    );
    if (task.isOverdue || risk === 'CRITICO') {
      pushTask(task, 'SLA vencido', 'critical');
    } else if (task.status === 'paused') {
      pushTask(task, 'Pausada', 'warning');
    } else if (risk === 'ALERTA') {
      pushTask(task, 'SLA crítico', 'warning');
    } else if (task.isRoleQueue && !task.assignedUserId) {
      pushTask(task, 'Sem responsável', 'warning');
    }
  }

  for (const item of unifiedWorkItems) {
    if (item.status === 'completed' || seen.has(item.source_id)) continue;
    if (item.is_overdue) {
      seen.add(item.source_id);
      items.push({
        id: item.source_id,
        taskId: item.source_id,
        title: item.title,
        reason: item.source === 'problem' ? 'Problema atrasado' : 'Demanda atrasada',
        slaDetail: 'Em atraso',
        tone: 'critical',
      });
    }
  }

  return items.sort((a, b) => (a.tone === 'critical' ? -1 : 1) - (b.tone === 'critical' ? -1 : 1));
}

export interface NearSlaItem {
  id: string;
  title: string;
  remainingMinutes: number;
  taskId: string;
}

export interface OverdueSlaItem {
  id: string;
  title: string;
  overdueDetail: string;
  taskId: string;
}

export function buildNearSlaItems(
  claimedTasks: TaskListItem[],
  queueTasks: TaskListItem[],
  thresholdMinutes = 120,
): NearSlaItem[] {
  const items: NearSlaItem[] = [];
  for (const task of [...claimedTasks, ...queueTasks]) {
    if (task.status === 'completed') continue;
    const remaining = getRemainingMinutesFromDeadline(task.slaDeadline);
    if (remaining === null || remaining < 0 || remaining > thresholdMinutes) continue;
    items.push({
      id: task.id,
      taskId: task.id,
      title: task.stageName ?? task.instanceTitle ?? 'Tarefa',
      remainingMinutes: remaining,
    });
  }
  return items.sort((a, b) => a.remainingMinutes - b.remainingMinutes).slice(0, 6);
}

export function buildOverdueSlaItems(
  claimedTasks: TaskListItem[],
  queueTasks: TaskListItem[],
  unifiedWorkItems: UnifiedWorkItem[],
): OverdueSlaItem[] {
  const items: OverdueSlaItem[] = [];
  const seen = new Set<string>();

  for (const task of [...claimedTasks, ...queueTasks]) {
    if (task.status === 'completed' || seen.has(task.id)) continue;
    const remaining = getRemainingMinutesFromDeadline(task.slaDeadline);
    if (!task.isOverdue && (remaining === null || remaining >= 0)) continue;
    seen.add(task.id);
    items.push({
      id: task.id,
      taskId: task.id,
      title: task.stageName ?? task.instanceTitle ?? 'Tarefa',
      overdueDetail: formatTaskSlaDetail(task),
    });
  }

  for (const item of unifiedWorkItems) {
    if (item.status === 'completed' || seen.has(item.source_id) || !item.is_overdue) continue;
    seen.add(item.source_id);
    items.push({
      id: item.source_id,
      taskId: item.source_id,
      title: item.title,
      overdueDetail: 'Em atraso',
    });
  }

  return items;
}

export interface BlockerItem {
  id: string;
  label: string;
  detail: string;
  taskId?: string;
  tone: 'critical' | 'warning';
}

export function buildBlockerItems(
  claimedTasks: TaskListItem[],
  queueTasks: TaskListItem[],
  checkWip: (stageId: string, stageName: string, all: TaskListItem[]) => { exceeded: boolean; current: number; limit: number },
): BlockerItem[] {
  const items: BlockerItem[] = [];
  const all = [...claimedTasks, ...queueTasks];

  for (const task of claimedTasks) {
    if (task.status !== 'paused') continue;
    const meta = (task.metadata ?? {}) as Record<string, unknown>;
    const reason = typeof meta.pause_reason === 'string' ? meta.pause_reason : 'Motivo não informado';
    items.push({
      id: `pause-${task.id}`,
      taskId: task.id,
      label: 'Tarefa pausada',
      detail: `${task.stageName ?? 'Etapa'} — ${reason}`,
      tone: 'warning',
    });
  }

  const stageKeys = new Set(all.map(t => `${t.stageId}::${t.stageName}`));
  for (const key of stageKeys) {
    const [stageId, stageName] = key.split('::');
    const wip = checkWip(stageId, stageName, all);
    if (wip.exceeded) {
      items.push({
        id: `wip-${stageId}`,
        label: 'Limite WIP atingido',
        detail: `${stageName}: ${wip.current}/${wip.limit} em execução`,
        tone: 'critical',
      });
    }
  }

  for (const task of all) {
    if (task.status !== 'waiting') continue;
    items.push({
      id: `wait-${task.id}`,
      taskId: task.id,
      label: 'Aguardando dependência',
      detail: task.stageName ?? task.instanceTitle ?? 'Tarefa bloqueada',
      tone: 'warning',
    });
  }

  return items.slice(0, 6);
}

export interface ShiftLoadForecast {
  activeCount: number;
  waitingCount: number;
  estimatedMinutes: number;
  utilizationPct: number;
  tone: 'ok' | 'warning' | 'critical';
  message: string;
  riskLabel: string;
  estimatedEndLabel: string | null;
}

export function formatTaskSlaDetail(task: TaskListItem): string {
  const remaining = getRemainingMinutesFromDeadline(task.slaDeadline);
  if (remaining === null) return 'Sem SLA definido';
  if (remaining < 0) {
    const overdueMinutes = Math.abs(remaining);
    const days = Math.floor(overdueMinutes / (60 * 24));
    const hours = Math.floor((overdueMinutes % (60 * 24)) / 60);
    if (days > 0) return `${days} dia${days > 1 ? 's' : ''} em atraso`;
    return `${hours}h em atraso`;
  }
  if (remaining < 60) return `${Math.round(remaining)} min restantes`;
  const h = Math.floor(remaining / 60);
  const m = Math.round(remaining % 60);
  return m > 0 ? `${h}h ${m}min restantes` : `${h}h restantes`;
}

export function buildShiftLoadForecast(
  claimedTasks: TaskListItem[],
  queueTasks: TaskListItem[],
  avgDurationMin: number,
  currentTaskId?: string | null,
  inboxRailTasks?: TaskListItem[],
): ShiftLoadForecast {
  const pool = inboxRailTasks
    ? collectOperationalTaskPool(inboxRailTasks, claimedTasks, queueTasks)
    : [...claimedTasks, ...queueTasks];
  const active = pool.filter((t) => t.status !== 'completed');
  const inProgressTasks = active.filter((t) => t.status === 'in_progress');

  const estimateTaskMinutes = (task: TaskListItem): number => {
    const planned = task.plannedDuration ?? avgDurationMin;
    return planned > 0 ? planned : avgDurationMin;
  };

  let estimatedMinutes = active.reduce((sum, t) => sum + estimateTaskMinutes(t), 0);

  if (estimatedMinutes === 0 && inProgressTasks.length > 0) {
    estimatedMinutes = inProgressTasks.reduce((sum, t) => sum + estimateTaskMinutes(t), 0);
  }

  let utilizationPct = Math.min(100, Math.round((estimatedMinutes / SHIFT_MINUTES) * 100));

  if (inProgressTasks.length > 0 && utilizationPct === 0) {
    const floorMinutes = Math.max(estimateTaskMinutes(inProgressTasks[0]!), 15);
    utilizationPct = Math.min(100, Math.max(5, Math.round((floorMinutes / SHIFT_MINUTES) * 100)));
    estimatedMinutes = Math.max(estimatedMinutes, floorMinutes);
  }

  const waiting = currentTaskId
    ? active.filter((t) => t.id !== currentTaskId)
    : active;

  const tone: ShiftLoadForecast['tone'] =
    utilizationPct >= 90 ? 'critical' : utilizationPct >= 65 ? 'warning' : 'ok';
  const message =
    inProgressTasks.length > 0 && waiting.length <= 1
      ? `${inProgressTasks.length} em execução — capacidade em uso`
      : waiting.length === 0
        ? 'Nenhum workflow aguardando além do foco'
        : utilizationPct >= 90
          ? 'Turno saturado — priorize conclusões antes de assumir novas demandas.'
          : utilizationPct >= 65
            ? 'Carga elevada — mantenha foco em um item por vez.'
            : 'Capacidade disponível — ritmo sustentável para o turno.';
  const riskLabel =
    tone === 'critical' ? 'Risco alto'
    : tone === 'warning' ? 'Atenção à carga'
    : inProgressTasks.length > 0 ? 'Em execução'
    : 'Operação estável';
  const estimatedEndLabel = estimatedMinutes > 0
    ? new Date(Date.now() + estimatedMinutes * 60_000).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : null;

  return {
    activeCount: active.length,
    waitingCount: waiting.length,
    estimatedMinutes,
    utilizationPct,
    tone,
    message,
    riskLabel,
    estimatedEndLabel,
  };
}

export function formatRemainingMinutes(minutes: number): string {
  if (minutes <= 0) return 'Vencido';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

export function formatShiftMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m} min`;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

const PRIORITY_LABELS: Record<string, string> = {
  critica: 'Crítica',
  alta: 'Alta',
  media: 'Média',
  baixa: 'Baixa',
};

function formatOriginalPriority(priority?: string | null): string {
  if (!priority) return '—';
  return PRIORITY_LABELS[priority.toLowerCase()] ?? priority;
}

export interface OperationalPriorityView {
  originalLabel: string;
  operationalLabel: string;
  escalated: boolean;
}

export function resolveOperationalPriority(task: TaskListItem): OperationalPriorityView {
  const originalLabel = formatOriginalPriority(task.priority);
  const remaining = getRemainingMinutesFromDeadline(task.slaDeadline);
  const risk = getOperationalRiskLevel(remaining, getTaskTotalSlaMinutes(task));
  const isOverdue = task.isOverdue || (remaining !== null && remaining < 0);

  if (isOverdue || risk === 'CRITICO') {
    return {
      originalLabel,
      operationalLabel: 'Crítica',
      escalated: originalLabel !== 'Crítica',
    };
  }
  if (risk === 'ALERTA') {
    const escalated = !['Crítica', 'Alta'].includes(originalLabel);
    return {
      originalLabel,
      operationalLabel: escalated ? 'Alta' : originalLabel,
      escalated,
    };
  }
  return { originalLabel, operationalLabel: originalLabel, escalated: false };
}

export function isTaskCriticallyAttentioned(task: TaskListItem): boolean {
  const remaining = getRemainingMinutesFromDeadline(task.slaDeadline);
  const risk = getOperationalRiskLevel(remaining, getTaskTotalSlaMinutes(task));
  return task.isOverdue || risk === 'CRITICO';
}

export function filterAttentionExcluding(
  items: AttentionItem[],
  excludeTaskId?: string | null,
): AttentionItem[] {
  if (!excludeTaskId) return items;
  return items.filter(item => item.taskId !== excludeTaskId);
}

export function collectOperationalTaskPool(
  inboxRailTasks: TaskListItem[],
  claimedTasks: TaskListItem[],
  queueTasks: TaskListItem[],
): TaskListItem[] {
  const seen = new Set<string>();
  const pool: TaskListItem[] = [];
  for (const task of inboxRailTasks) {
    if (seen.has(task.id)) continue;
    seen.add(task.id);
    pool.push(task);
  }
  for (const task of [...claimedTasks, ...queueTasks]) {
    if (seen.has(task.id)) continue;
    seen.add(task.id);
    pool.push(task);
  }
  return pool;
}

export function countInProgressTasks(
  claimedTasks: TaskListItem[],
  queueTasks: TaskListItem[],
  inboxRailTasks?: TaskListItem[],
): number {
  const pool = inboxRailTasks
    ? collectOperationalTaskPool(inboxRailTasks, claimedTasks, queueTasks)
    : [...claimedTasks, ...queueTasks];
  return pool.filter((t) => t.status === 'in_progress').length;
}

export function countOpenDemands(inboxRailTasks: TaskListItem[]): number {
  return inboxRailTasks.filter((t) => t.status !== 'completed').length;
}

export function countBlockedDemands(
  claimedTasks: TaskListItem[],
  queueTasks: TaskListItem[],
  blockers: BlockerItem[],
  inboxRailTasks?: TaskListItem[],
): number {
  if (blockers.length > 0) return blockers.length;
  const pool = inboxRailTasks
    ? collectOperationalTaskPool(inboxRailTasks, claimedTasks, queueTasks)
    : [...claimedTasks, ...queueTasks];
  return pool.filter((t) => t.status === 'paused' || t.status === 'waiting').length;
}

export function countCriticalDemands(attentionItems: AttentionItem[]): number {
  return attentionItems.filter((i) => i.tone === 'critical').length;
}

export function countCompletedToday(unifiedWorkItems: UnifiedWorkItem[]): number {
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  return unifiedWorkItems.filter((i) => {
    if (i.status !== 'completed' || !i.completed_at) return false;
    return new Date(i.completed_at).getTime() >= todayStart.getTime();
  }).length;
}

export interface SlaRateKpi {
  display: string;
  numeric: number | null;
  tone: 'default' | 'ok' | 'warning';
}

/** SLA agregado 7d — nunca exibe "0%" quando não há dado ou SLA definido. */
export function formatSlaRateKpi(rate: number | null | undefined): SlaRateKpi {
  if (rate == null || Number.isNaN(rate) || rate <= 0) {
    return { display: 'Sem SLA', numeric: null, tone: 'default' };
  }
  const rounded = Math.round(rate);
  return {
    display: `${rounded}%`,
    numeric: rounded,
    tone: rounded >= 80 ? 'ok' : 'default',
  };
}

export interface ExecutionCenterKpis {
  openDemands: number;
  inProgress: number;
  critical: number;
  blocked: number;
  doneToday: number;
  sla: SlaRateKpi;
  shiftLoad: ShiftLoadForecast;
}

export function buildExecutionCenterKpis(args: {
  claimedTasks: TaskListItem[];
  queueTasks: TaskListItem[];
  inboxRailTasks: TaskListItem[];
  unifiedWorkItems: UnifiedWorkItem[];
  attentionItems: AttentionItem[];
  blockers: BlockerItem[];
  avgDurationMin: number;
  nextTaskId?: string | null;
  slaRate7d?: number | null;
}): ExecutionCenterKpis {
  const pool = collectOperationalTaskPool(args.inboxRailTasks, args.claimedTasks, args.queueTasks);
  let inProgress = pool.filter((t) => t.status === 'in_progress').length;
  const nextTask = args.nextTaskId ? pool.find((t) => t.id === args.nextTaskId) : null;
  if (nextTask?.status === 'in_progress' && inProgress === 0) {
    inProgress = 1;
  }

  const shiftLoad = buildShiftLoadForecast(
    args.claimedTasks,
    args.queueTasks,
    args.avgDurationMin,
    args.nextTaskId,
    args.inboxRailTasks,
  );

  return {
    openDemands: countOpenDemands(args.inboxRailTasks),
    inProgress,
    critical: countCriticalDemands(args.attentionItems),
    blocked: countBlockedDemands(args.claimedTasks, args.queueTasks, args.blockers, args.inboxRailTasks),
    doneToday: countCompletedToday(args.unifiedWorkItems),
    sla: formatSlaRateKpi(args.slaRate7d),
    shiftLoad,
  };
}

export function countOverdueTasks(
  claimedTasks: TaskListItem[],
  queueTasks: TaskListItem[],
  unifiedWorkItems: UnifiedWorkItem[],
): number {
  return buildOverdueSlaItems(claimedTasks, queueTasks, unifiedWorkItems).length;
}

export interface CarteiraPreviewItem {
  id: string;
  title: string;
  statusLabel: string;
  slaDetail: string;
  priorityLabel: string;
}

export interface RecentExecutionItem {
  id: string;
  title: string;
  whenLabel: string;
  sourceLabel: string;
}

export interface RecentCommentItem {
  id: string;
  taskId: string;
  taskTitle: string;
  excerpt: string;
  whenLabel: string;
}

export interface RecentAttachmentItem {
  id: string;
  taskId: string;
  taskTitle: string;
  fileName: string;
  whenLabel: string;
}

export interface ActivityHistoryItem {
  id: string;
  title: string;
  detail?: string;
  whenLabel: string;
  tone?: 'default' | 'ok' | 'info' | 'warning';
}

export interface RecentEventItem {
  id: string;
  taskId: string;
  taskTitle: string;
  eventLabel: string;
  whenLabel: string;
  tone?: 'default' | 'ok' | 'info' | 'warning';
}

export interface RecentProblemItem {
  id: string;
  title: string;
  statusLabel: string;
  whenLabel: string;
  severityLabel?: string;
}

export interface OperationalTimelineItem {
  id: string;
  title: string;
  detail?: string;
  whenLabel: string;
  tone?: 'default' | 'ok' | 'info' | 'warning';
}

export interface OperationalContextContent {
  recentExecutions: RecentExecutionItem[];
  recentComments: RecentCommentItem[];
  recentAttachments: RecentAttachmentItem[];
  focusActivityHistory: ActivityHistoryItem[];
  recentEvents: RecentEventItem[];
  recentProblems: RecentProblemItem[];
  operationalTimeline: OperationalTimelineItem[];
}

const OPERATIONAL_EVENT_LABELS: Record<TaskOperationalHistoryEntry['event'], string> = {
  activated: 'Demanda ativada',
  claimed: 'Demanda assumida',
  started: 'Execução iniciada',
  paused: 'Execução pausada',
  resumed: 'Execução retomada',
  completed: 'Execução concluída',
  failed: 'Execução com falha',
  skipped: 'Etapa pulada',
};

function sourceShortLabel(source: UnifiedWorkItem['source']): string {
  if (source === 'action') return 'Ação';
  if (source === 'problem') return 'Problema';
  if (source === 'monitoring') return 'Monitor';
  return 'Workflow';
}

function taskTitle(task: TaskListItem): string {
  return task.stageName ?? task.instanceTitle ?? 'Demanda';
}

function mapOperationalEventTone(event: TaskOperationalHistoryEntry['event']): ActivityHistoryItem['tone'] {
  if (event === 'completed') return 'ok';
  if (event === 'failed') return 'warning';
  if (event === 'paused') return 'warning';
  if (event === 'started' || event === 'resumed' || event === 'claimed') return 'info';
  return 'default';
}

export function buildNextDemandsPreview(
  inboxRailTasks: TaskListItem[],
  excludeTaskId?: string | null,
): CarteiraPreviewItem[] {
  const filtered = inboxRailTasks.filter((t) => t.id !== excludeTaskId && t.status !== 'completed');
  const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
  const recent = filtered
    .filter((t) => new Date(t.createdAt).getTime() >= dayAgo)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const rest = filtered.filter((t) => !recent.some((r) => r.id === t.id));
  return [...recent, ...rest]
    .slice(0, 12)
    .map((t) => ({
      id: t.id,
      title: taskTitle(t),
      statusLabel:
        t.status === 'in_progress' ? 'Em execução'
        : t.status === 'paused' ? 'Pausada'
        : t.status === 'waiting' ? 'Aguardando'
        : 'Pendente',
      slaDetail: formatTaskSlaDetail(t),
      priorityLabel: resolveOperationalPriority(t).operationalLabel,
    }));
}

export function buildOperationalContextContent(args: {
  claimedTasks: TaskListItem[];
  queueTasks: TaskListItem[];
  inboxRailTasks: TaskListItem[];
  unifiedWorkItems: UnifiedWorkItem[];
  nextTask: TaskListItem | null;
}): OperationalContextContent {
  const pool = collectOperationalTaskPool(args.inboxRailTasks, args.claimedTasks, args.queueTasks);
  const focus = args.nextTask;

  const recentExecutions = [...args.unifiedWorkItems]
    .filter((i) => i.status === 'completed' && i.completed_at)
    .sort((a, b) => new Date(b.completed_at!).getTime() - new Date(a.completed_at!).getTime())
    .slice(0, 6)
    .map((i) => ({
      id: i.source_id,
      title: i.title,
      whenLabel: formatRelativeTime(i.completed_at),
      sourceLabel: sourceShortLabel(i.source),
    }));

  const recentComments = pool
    .filter((t) => Boolean(t.notes?.trim()))
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 6)
    .map((t) => ({
      id: `${t.id}-note`,
      taskId: t.id,
      taskTitle: taskTitle(t),
      excerpt: t.notes!.trim().slice(0, 140),
      whenLabel: formatRelativeTime(t.updatedAt),
    }));

  const recentAttachments = pool
    .flatMap((t) => {
      const raw = t.metadata?.attachments;
      if (!Array.isArray(raw)) return [];
      return raw.map((file, index) => {
        const record = file as { id?: string; name?: string; createdAt?: string };
        return {
          id: record.id ?? `${t.id}-att-${index}`,
          taskId: t.id,
          taskTitle: taskTitle(t),
          fileName: record.name ?? 'Anexo',
          whenLabel: formatRelativeTime(record.createdAt ?? t.updatedAt),
          sortAt: new Date(record.createdAt ?? t.updatedAt).getTime(),
        };
      });
    })
    .sort((a, b) => b.sortAt - a.sortAt)
    .slice(0, 6)
    .map(({ sortAt: _sortAt, ...item }) => item);

  const focusActivityHistory: ActivityHistoryItem[] = [];
  if (focus) {
    const history = Array.isArray(focus.metadata?.operationalHistory)
      ? focus.metadata!.operationalHistory as TaskOperationalHistoryEntry[]
      : [];
    if (history.length > 0) {
      for (const [index, entry] of [...history].reverse().slice(0, 8).entries()) {
        focusActivityHistory.push({
          id: `${focus.id}-hist-${index}`,
          title: OPERATIONAL_EVENT_LABELS[entry.event] ?? entry.event,
          detail: taskTitle(focus),
          whenLabel: formatRelativeTime(entry.capturedAt),
          tone: mapOperationalEventTone(entry.event),
        });
      }
    } else {
      focusActivityHistory.push(
        {
          id: `${focus.id}-created`,
          title: 'Demanda aberta',
          detail: taskTitle(focus),
          whenLabel: formatRelativeTime(focus.createdAt),
          tone: 'default',
        },
        {
          id: `${focus.id}-updated`,
          title: 'Última movimentação',
          detail: formatTaskSlaDetail(focus),
          whenLabel: formatRelativeTime(focus.updatedAt ?? focus.createdAt),
          tone: focus.isOverdue ? 'warning' : 'default',
        },
      );
    }
  }

  const recentEvents = pool
    .flatMap((t) => {
      const history = Array.isArray(t.metadata?.operationalHistory)
        ? t.metadata!.operationalHistory as TaskOperationalHistoryEntry[]
        : [];
      return history.map((entry, index) => ({
        id: `${t.id}-evt-${index}-${entry.capturedAt}`,
        taskId: t.id,
        taskTitle: taskTitle(t),
        eventLabel: OPERATIONAL_EVENT_LABELS[entry.event] ?? entry.event,
        whenLabel: formatRelativeTime(entry.capturedAt),
        tone: mapOperationalEventTone(entry.event),
        sortAt: new Date(entry.capturedAt).getTime(),
      }));
    })
    .sort((a, b) => b.sortAt - a.sortAt)
    .slice(0, 8)
    .map(({ sortAt: _sortAt, ...item }) => item);

  const recentProblems = [...args.unifiedWorkItems]
    .filter((i) => i.source === 'problem' && i.status !== 'completed')
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 6)
    .map((i) => ({
      id: i.source_id,
      title: i.title,
      statusLabel: i.source_context?.issue_status ?? 'Aberto',
      whenLabel: formatRelativeTime(i.created_at),
      severityLabel: i.source_context?.issue_severity ?? undefined,
    }));

  const operationalTimeline: OperationalTimelineItem[] = [
    ...pool.flatMap((t) => {
      const history = Array.isArray(t.metadata?.operationalHistory)
        ? t.metadata!.operationalHistory as TaskOperationalHistoryEntry[]
        : [];
      return history.map((entry, index) => ({
        id: `tl-${t.id}-${index}`,
        title: `${OPERATIONAL_EVENT_LABELS[entry.event] ?? entry.event} · ${taskTitle(t)}`,
        detail: taskTitle(t),
        whenLabel: formatRelativeTime(entry.capturedAt),
        tone: mapOperationalEventTone(entry.event),
        sortAt: new Date(entry.capturedAt).getTime(),
      }));
    }),
    ...pool
      .filter((t) => t.status === 'completed' && t.completedAt)
      .map((t) => ({
        id: `tl-done-${t.id}`,
        title: `Conclusão · ${taskTitle(t)}`,
        detail: formatTaskSlaDetail(t),
        whenLabel: formatRelativeTime(t.completedAt),
        tone: 'ok' as const,
        sortAt: new Date(t.completedAt!).getTime(),
      })),
    ...pool
      .filter((t) => Boolean(t.notes?.trim()))
      .map((t) => ({
        id: `tl-note-${t.id}`,
        title: `Observação · ${taskTitle(t)}`,
        detail: t.notes!.trim().slice(0, 120),
        whenLabel: formatRelativeTime(t.updatedAt),
        tone: 'info' as const,
        sortAt: new Date(t.updatedAt).getTime(),
      })),
  ]
    .sort((a, b) => b.sortAt - a.sortAt)
    .slice(0, 10)
    .map(({ sortAt: _sortAt, ...item }) => item);

  return {
    recentExecutions,
    recentComments,
    recentAttachments,
    focusActivityHistory,
    recentEvents,
    recentProblems,
    operationalTimeline,
  };
}

export interface CoherenceAuditInput {
  nextTask: TaskListItem | null;
  inProgressCount: number;
  overdueCount: number;
  overdueSlaCount: number;
  attentionCount: number;
  waitingQueueCount: number;
  shiftUtilizationPct: number;
  blockerCount: number;
  openDemandsCount?: number;
}

export function auditExecutionCenterCoherence(input: CoherenceAuditInput): void {
  if (import.meta.env.PROD) return;

  const issues: string[] = [];

  if (input.nextTask?.status === 'in_progress' && input.inProgressCount === 0) {
    issues.push('Foco em atividade em execução, mas KPI "Em execução" = 0');
  }
  if (input.inProgressCount > 0 && input.shiftUtilizationPct === 0) {
    issues.push('Há atividade em execução, mas carga do turno = 0%');
  }
  if (input.nextTask?.status === 'in_progress' && input.shiftUtilizationPct === 0) {
    issues.push('Foco em execução ativa, mas carga do turno = 0%');
  }
  if (input.nextTask && isTaskCriticallyAttentioned(input.nextTask)) {
    const op = resolveOperationalPriority(input.nextTask);
    if (!op.escalated && op.originalLabel === 'Baixa' && input.nextTask.isOverdue) {
      issues.push('Demanda atrasada com prioridade original baixa sem reclassificação operacional');
    }
  }
  if (input.overdueSlaCount > 0 && input.attentionCount === 0 && !input.nextTask) {
    issues.push('Itens vencidos sem alerta em Atenção Imediata');
  }
  if (input.waitingQueueCount === 0 && input.shiftUtilizationPct >= 65) {
    issues.push('Fila vazia com carga alta reportada');
  }
  if (input.blockerCount > 0 && input.attentionCount === 0 && !input.nextTask) {
    issues.push('Bloqueios ativos sem alerta em Atenção Imediata');
  }
  if (input.overdueSlaCount > 0 && input.overdueCount === 0) {
    issues.push('Itens vencidos no painel SLA, mas KPI de atrasadas = 0');
  }

  if (input.openDemandsCount != null && input.openDemandsCount === 0 && input.nextTask) {
    issues.push('Foco ativo com carteira vazia no resumo');
  }

  if (issues.length > 0) {
    console.warn('[MinhaMesa:Coerência]', issues);
  }
}
